import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { ExternalLink, Clock, FileText, Image, AlertCircle, ChevronRight } from 'lucide-react';
import type { SEOPage } from '@/shared/types';
import { ScoreCircle } from './ScoreCircle';

interface PageCardProps {
  page: SEOPage;
  projectId: string;
}

export function PageCard({ page, projectId }: PageCardProps) {
  const getUrlPath = (url: string) => {
    try {
      const parsed = new URL(url);
      return parsed.pathname || '/';
    } catch {
      return url;
    }
  };

  const issueCount = page.issues.length + page.warnings.length;

  return (
    <Link
      to={`/projects/${projectId}/pages/${page.id}`}
      className="card hover:shadow-md transition-shadow group"
    >
      <div className="flex items-start gap-4">
        <ScoreCircle score={page.seoScore} size="sm" showLabel={false} />

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="font-medium text-surface-900 truncate group-hover:text-brand-600 transition-colors">
                {page.title || 'Untitled Page'}
              </h3>
              <p className="text-sm text-surface-500 truncate flex items-center gap-1">
                <ExternalLink className="w-3 h-3 flex-shrink-0" />
                {getUrlPath(page.url)}
              </p>
            </div>
            <ChevronRight className="w-5 h-5 text-surface-400 group-hover:text-brand-600 flex-shrink-0" />
          </div>

          <div className="flex items-center gap-4 mt-3 text-xs text-surface-500">
            <span className="flex items-center gap-1">
              <FileText className="w-3.5 h-3.5" />
              {page.wordCount} words
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              {page.loadTimeMs ? `${(page.loadTimeMs / 1000).toFixed(1)}s` : '-'}
            </span>
            <span className="flex items-center gap-1">
              <Image className="w-3.5 h-3.5" />
              {page.totalImages} images
            </span>
            {issueCount > 0 && (
              <span className="flex items-center gap-1 text-red-600">
                <AlertCircle className="w-3.5 h-3.5" />
                {issueCount} issues
              </span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}

interface PageTableProps {
  pages: SEOPage[];
  projectId: string;
}

export function PageTable({ pages, projectId }: PageTableProps) {
  const getUrlPath = (url: string) => {
    try {
      const parsed = new URL(url);
      return parsed.pathname || '/';
    } catch {
      return url;
    }
  };

  return (
    <div className="table-container bg-white">
      <table className="table">
        <thead>
          <tr>
            <th>Page</th>
            <th>Score</th>
            <th>Issues</th>
            <th>Word Count</th>
            <th>Load Time</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {pages.map(page => {
            const issueCount = page.issues.length + page.warnings.length;

            return (
              <tr key={page.id}>
                <td>
                  <Link
                    to={`/projects/${projectId}/pages/${page.id}`}
                    className="block hover:text-brand-600"
                  >
                    <div className="font-medium text-surface-900 line-clamp-1">
                      {page.title || 'Untitled'}
                    </div>
                    <div className="text-xs text-surface-500 line-clamp-1">
                      {getUrlPath(page.url)}
                    </div>
                  </Link>
                </td>
                <td>
                  <ScoreCircle score={page.seoScore} size="sm" showLabel={false} />
                </td>
                <td>
                  {issueCount > 0 ? (
                    <span className="flex items-center gap-1 text-red-600">
                      <AlertCircle className="w-4 h-4" />
                      {issueCount}
                    </span>
                  ) : (
                    <span className="text-green-600">None</span>
                  )}
                </td>
                <td>{page.wordCount.toLocaleString()}</td>
                <td>
                  {page.loadTimeMs ? `${(page.loadTimeMs / 1000).toFixed(1)}s` : '-'}
                </td>
                <td>
                  <span
                    className={clsx(
                      'badge',
                      page.status === 'analyzed' && 'badge-completed',
                      page.status === 'crawling' && 'badge-crawling',
                      page.status === 'pending' && 'badge-pending',
                      page.status === 'failed' && 'badge-failed'
                    )}
                  >
                    {page.status}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
